const AXIS_WORDS = {
  warmth: {
    essence: ['Ember', 'Kindred', 'Hearth', 'Afterglow'],
    place: ['Sanctum', 'Harbor', 'Choir', 'Refuge'],
    mood: 'Radiant intimacy',
    color: '#ffb36b'
  },
  futurity: {
    essence: ['Signal', 'Tomorrow', 'Neon', 'Relay'],
    place: ['Verge', 'Frontier', 'Spire', 'Passage'],
    mood: 'Forward longing',
    color: '#6bbcff'
  },
  chaos: {
    essence: ['Storm', 'Fracture', 'Feral', 'Riot'],
    place: ['Wilds', 'Surge', 'Break', 'Frontier'],
    mood: 'Turbulent urgency',
    color: '#ff7ac8'
  },
  sacredness: {
    essence: ['Halo', 'Ritual', 'Altar', 'Vigil'],
    place: ['Cathedral', 'Shrine', 'Procession', 'Reliquary'],
    mood: 'Reverent gravity',
    color: '#f8d66d'
  },
  organicness: {
    essence: ['Verdant', 'Root', 'Moss', 'Bloom'],
    place: ['Garden', 'Grove', 'Estuary', 'Commons'],
    mood: 'Living tenderness',
    color: '#6fe3a1'
  },
  nostalgia: {
    essence: ['Memory', 'Afterimage', 'Sepia', 'Keepsake'],
    place: ['Archive', 'Station', 'Reliquary', 'Parlor'],
    mood: 'Haunted warmth',
    color: '#cfa9ff'
  },
  surrealism: {
    essence: ['Dream', 'Mirage', 'Impossible', 'Velvet'],
    place: ['Fold', 'Passage', 'Mirror', 'Aperture'],
    mood: 'Impossible drift',
    color: '#8aa4ff'
  },
  isolation: {
    essence: ['Silent', 'Far', 'Lone', 'Remote'],
    place: ['Reach', 'Margin', 'Exile', 'Outpost'],
    mood: 'Remote stillness',
    color: '#93a0c3'
  },
  emotionalSaturation: {
    essence: ['Velvet', 'Fever', 'Crimson', 'Chorus'],
    place: ['Bloom', 'Furnace', 'Theater', 'Pulse'],
    mood: 'Overbright feeling',
    color: '#ff8b9d'
  },
  corporateDensity: {
    essence: ['Chrome', 'Glass', 'Protocol', 'Civic'],
    place: ['Regime', 'Atrium', 'Grid', 'Forum'],
    mood: 'Structured pressure',
    color: '#93d0ff'
  }
}

const AXIS_LABELS = {
  warmth: 'Warmth',
  futurity: 'Futurity',
  chaos: 'Chaos',
  sacredness: 'Sacredness',
  organicness: 'Organicness',
  nostalgia: 'Nostalgia',
  surrealism: 'Surrealism',
  isolation: 'Isolation',
  emotionalSaturation: 'Emotional Saturation',
  corporateDensity: 'Corporate Density'
}

const STOP_WORDS = new Set(['and', 'the', 'of', 'digital'])

export default class SymbolicTerritory {
  constructor() {
    this.territories = []
    this.byId = new Map()
  }

  update(clusters) {
    const usedTitles = new Set()

    this.territories = clusters.map(cluster => this.createTerritory(cluster, usedTitles))
    this.byId = new Map(this.territories.map(territory => [territory.id, territory]))
  }

  createTerritory(cluster, usedTitles) {
    const [primaryAxis, secondaryAxis, tertiaryAxis] = cluster.dominantAxes
    const primaryWords = AXIS_WORDS[primaryAxis.axis]
    const secondaryWords = AXIS_WORDS[(secondaryAxis || primaryAxis).axis]
    const tertiaryWords = AXIS_WORDS[(tertiaryAxis || secondaryAxis || primaryAxis).axis]
    const anchor = this.extractAnchor(cluster.strongestVibes)
    const title = this.ensureUniqueTitle(this.buildTitle({
      cluster,
      anchor,
      primaryWords,
      secondaryWords,
      tertiaryWords
    }), usedTitles, tertiaryWords)
    const dominantMood = this.buildDominantMood(primaryWords, secondaryWords, cluster)
    const archetype = this.buildArchetype(primaryWords, secondaryWords, cluster)
    const dominantAxes = cluster.dominantAxes.map(entry => ({
      axis: entry.axis,
      label: entry.label,
      value: entry.value
    }))
    const compatibilityProfile = this.buildCompatibilityProfile(cluster)
    const symbolicDescription = `${title} coheres around ${primaryAxis.label.toLowerCase()} and ${secondaryAxis.label.toLowerCase()}, with ${cluster.strongestVibes[0]} acting as one of its clearest carriers.`

    return {
      ...cluster,
      title,
      dominantMood,
      archetype,
      dominantAxes,
      compatibilityProfile,
      symbolicDescription,
      color: this.mixColors(primaryWords.color, secondaryWords.color, 0.42 + cluster.tension * 0.14),
      labelMood: secondaryWords.mood,
      nameAnchor: anchor
    }
  }

  buildTitle({ cluster, anchor, primaryWords, secondaryWords, tertiaryWords }) {
    const signature = (cluster.members.length + Math.round(cluster.cohesion * 10) + Math.round(cluster.tension * 10)) % 5

    if (signature === 0) {
      return `${primaryWords.essence[0]} ${secondaryWords.place[0]}`
    }

    if (signature === 1) {
      return `${secondaryWords.essence[1]} ${primaryWords.place[1]}`
    }

    if (signature === 2) {
      return `${primaryWords.place[2]} of ${anchor || secondaryWords.essence[2]}`
    }

    if (signature === 3) {
      return `${anchor || tertiaryWords.essence[0]} ${primaryWords.place[0]}`
    }

    return `${primaryWords.essence[3]} of the ${secondaryWords.place[3]}`
  }

  ensureUniqueTitle(title, usedTitles, tertiaryWords) {
    if (!usedTitles.has(title)) {
      usedTitles.add(title)
      return title
    }

    const alternate = `${title} ${tertiaryWords.essence[1]}`
    usedTitles.add(alternate)
    return alternate
  }

  extractAnchor(strongestVibes) {
    const source = strongestVibes.find(Boolean) || ''
    const token = source
      .split(/\s+/)
      .map(part => part.replace(/[^a-zA-Z]/g, ''))
      .find(part => part.length > 3 && !STOP_WORDS.has(part.toLowerCase()))

    return token ? token[0].toUpperCase() + token.slice(1) : ''
  }

  buildDominantMood(primaryWords, secondaryWords, cluster) {
    if (cluster.tension > 0.24) {
      return `${primaryWords.mood} under ${secondaryWords.mood.toLowerCase()}`
    }

    return `${primaryWords.mood} with ${secondaryWords.mood.toLowerCase()}`
  }

  buildArchetype(primaryWords, secondaryWords, cluster) {
    if (cluster.cohesion > 0.86) {
      return `${primaryWords.place[0].toLowerCase()} of ${secondaryWords.essence[0].toLowerCase()} ritual`
    }

    if (cluster.tension > 0.26) {
      return `${secondaryWords.place[0].toLowerCase()} under ${primaryWords.essence[1].toLowerCase()} pressure`
    }

    return `${primaryWords.place[1].toLowerCase()} of ${secondaryWords.mood.toLowerCase()}`
  }

  buildCompatibilityProfile(cluster) {
    const affinities = cluster.dominantAxes.slice(0, 2).map(entry => `Welcomes ${AXIS_LABELS[entry.axis].toLowerCase()}-leaning atmospheres`)
    const frictions = cluster.weakestAxes.map(entry => `Resists ${AXIS_LABELS[entry.axis].toLowerCase()}-driven pressure`)

    return {
      affinities,
      frictions
    }
  }

  mixColors(leftHex, rightHex, ratio) {
    const left = this.hexToRgb(leftHex)
    const right = this.hexToRgb(rightHex)

    return `rgb(${Math.round(left.r + (right.r - left.r) * ratio)}, ${Math.round(left.g + (right.g - left.g) * ratio)}, ${Math.round(left.b + (right.b - left.b) * ratio)})`
  }

  hexToRgb(hex) {
    const normalized = hex.replace('#', '')
    const value = normalized.length === 3
      ? normalized.split('').map(char => char + char).join('')
      : normalized
    const int = Number.parseInt(value, 16)

    return {
      r: (int >> 16) & 255,
      g: (int >> 8) & 255,
      b: int & 255
    }
  }

  getTerritories() {
    return this.territories
  }

  getById(id) {
    return this.byId.get(id) ?? null
  }
}
